/**
 * `contacts.html`'s Anniversaries dialog, ported onto the real
 * `GET /contacts/anniversaries` digest (added alongside this screen — see
 * `salescrmapi/docs/modules/contacts.model.md`) instead of the prototype's
 * in-memory fixture.
 *
 * "Send wishes" there composed a fake email through a fake transport. There is
 * no email module (§8) wired up here, so this logs a real, completed activity
 * against the contact instead — an honest record that outreach happened,
 * using the activities API every other screen in this app already relies on,
 * rather than pretending to send something that goes nowhere.
 */
import { useMemo, useState } from 'react';
import { Modal, EmptyState } from '../../components/common/index.jsx';
import { Spinner, ButtonSpinner } from '../../components/common/Loading.jsx';
import { useContactAnniversaries } from './useContacts.js';
import { useCreateActivity } from '../activities/useActivities.js';

const RANGES = [7, 30, 90];

const KIND_META = {
  birthday: { icon: 'bi-balloon-fill', colour: '#f43f5e' },
  work_anniversary: { icon: 'bi-briefcase-fill', colour: '#6366f1' },
  customer_anniversary: { icon: 'bi-award-fill', colour: '#10b981' },
};

function whenLabel(days) {
  if (days === 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  return `In ${days} days`;
}

function urgencyPill(days) {
  if (days === 0) return 'pill-rose';
  if (days <= 7) return 'pill-amber';
  return 'pill-gray';
}

const BUCKETS = [
  ['Today', (e) => e.days_until === 0],
  ['This week', (e) => e.days_until > 0 && e.days_until <= 7],
  ['Later this month', (e) => e.days_until > 7 && e.days_until <= 31],
  ['Beyond', (e) => e.days_until > 31],
];

export function AnniversariesModal({ open, onClose }) {
  const [days, setDays] = useState(30);
  const [kinds, setKinds] = useState({ birthday: true, work_anniversary: true, customer_anniversary: true });
  const [wished, setWished] = useState(() => new Set());

  const { data, isLoading, error } = useContactAnniversaries(days, open);
  const create = useCreateActivity();

  const entries = useMemo(
    () => (data?.data ?? []).filter((e) => kinds[e.kind]),
    [data, kinds],
  );

  const logWish = async (entry) => {
    const key = `${entry.contact.id}|${entry.kind}`;
    await create.mutateAsync({
      contact_id: entry.contact.id,
      /* crm.activity requires a primary link — lead/account/opportunity/
         quotation/campaign — a contact alone is not one (§8: "a call is still
         about a company or a deal"). The contact's own account is the natural
         anchor here. */
      account_id: entry.contact.account.id,
      kind: 'email',
      subject: `${entry.label} wishes — ${entry.contact.full_name}`,
      state: 'completed',
      completed_at: new Date().toISOString(),
    });
    setWished((prev) => new Set(prev).add(key));
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Upcoming Anniversaries"
      size="lg"
      footer={<button type="button" className="btn btn-ghost btn-sm-x" onClick={onClose}>Close</button>}
    >
      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <div className="seg">
          {RANGES.map((d) => (
            <button key={d} type="button" className={days === d ? 'active' : ''} onClick={() => setDays(d)}>
              {d} days
            </button>
          ))}
        </div>
        <div className="d-flex flex-wrap gap-1">
          {Object.entries(KIND_META).map(([k, meta]) => (
            <button
              key={k} type="button" className={`chip ${kinds[k] ? 'active' : ''}`}
              onClick={() => setKinds((prev) => ({ ...prev, [k]: !prev[k] }))}
            >
              <i className={`bi ${meta.icon} me-1`} aria-hidden="true" />
              {k === 'birthday' ? 'Birthdays' : k === 'work_anniversary' ? 'Work' : 'Customer'}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <Spinner />
      ) : error ? (
        <p className="t-sm text-muted">Could not load anniversaries.</p>
      ) : entries.length === 0 ? (
        <EmptyState icon="bi-check-lg" title="Nothing coming up" message="No anniversaries fall in this window. Try a longer range." />
      ) : (
        BUCKETS.map(([label, test]) => {
          const items = entries.filter(test);
          if (!items.length) return null;
          return (
            <div key={label} className="mb-3">
              <div className="act-group-head">{label} <span className="pill pill-gray ms-1">{items.length}</span></div>
              {items.map((e) => {
                const key = `${e.contact.id}|${e.kind}`;
                const isWished = wished.has(key);
                const meta = KIND_META[e.kind];
                return (
                  <div key={key} className={`act-card${isWished ? ' opacity-75' : ''}`}>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <div className="ac-ico" style={{ background: meta.colour }}>
                        <i className={`bi ${meta.icon}`} aria-hidden="true" />
                      </div>
                      <div className="min-w-0" style={{ flex: '1 1 220px' }}>
                        <div className="t-sm fw-650 text-truncate">
                          {e.contact.full_name}
                          {e.contact.job_title && <span className="ink-3 fw-450"> · {e.contact.job_title}</span>}
                        </div>
                        <div className="t-xs ink-3 text-truncate">
                          {e.label}{e.years ? ` · ${e.years} year${e.years === 1 ? '' : 's'}` : ''}
                          {e.contact.account ? ` · ${e.contact.account.name}` : ''}
                        </div>
                      </div>
                      <div className="text-end">
                        <span className={`pill ${urgencyPill(e.days_until)}`}>{whenLabel(e.days_until)}</span>
                        <div className="t-2xs ink-4 mt-1">{e.next_occurrence}{e.adjusted ? ' · 29 Feb' : ''}</div>
                      </div>
                      {isWished ? (
                        <button type="button" className="btn btn-ghost btn-sm-x no-wrap" disabled>
                          <i className="bi bi-check-circle-fill text-success me-1" aria-hidden="true" />Logged
                        </button>
                      ) : !e.contact.account ? (
                        <button
                          type="button" className="btn btn-ghost btn-sm-x no-wrap" disabled
                          title="An activity needs a company or deal to attach to — this contact has no account on file."
                        >
                          No account to log against
                        </button>
                      ) : (
                        <button
                          type="button" className="btn btn-brand btn-sm-x no-wrap"
                          disabled={create.isPending} onClick={() => logWish(e)}
                        >
                          {create.isPending ? <ButtonSpinner /> : <i className="bi bi-envelope-heart me-1" aria-hidden="true" />}
                          Log wishes sent
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })
      )}
    </Modal>
  );
}
